// FirstDateChangeComponent.jsx
import React, { useState } from 'react';
import {
  Form,
  Button,
  Table,
  Card,
  Alert,
  Spinner,
  Row,
  Col,
  Modal,
} from 'react-bootstrap';
import { firstDateService } from './firstDateService';

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

/** Форматує дату для відображення (dd.mm.yyyy) */
const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('uk-UA');
};

/** Перетворює ISO-рядок або Date на формат YYYY-MM-DD для <input type="date"> */
const toInputDate = (value) => {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
};

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

const FirstDateChangeComponent = () => {
  // ── Пошук ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Повідомлення ────────────────────────────────────────────
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // ── Модальне вікно ──────────────────────────────────────────
  const [selectedApp, setSelectedApp] = useState(null);
  const [newFirstDate, setNewFirstDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [modalError, setModalError] = useState(null);

  // ── Пошук заявок ────────────────────────────────────────────
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setApplications([]);

    try {
      const result = await firstDateService.getApplicationsByBirthCertificate(searchQuery.trim());
      const data = result.data || [];
      setApplications(data);

      if (data.length === 0) {
        setError('Заявок за вказаним номером свідоцтва не знайдено.');
      }
    } catch (err) {
      setError('Помилка при завантаженні заявок. Спробуйте пізніше.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Відкрити модальне вікно для заявки ──────────────────────
  const handleOpenModal = (app) => {
    setSelectedApp(app);
    setNewFirstDate(toInputDate(app.firstDate));
    setModalError(null);
  };

  const handleCloseModal = () => {
    if (isUpdating) return;
    setSelectedApp(null);
    setNewFirstDate('');
    setModalError(null);
  };

  // ── Оновити firstDate ────────────────────────────────────────
  const handleUpdateFirstDate = async () => {
    if (!selectedApp || !newFirstDate) return;

    setIsUpdating(true);
    setModalError(null);

    try {
      await firstDateService.updateFirstDate(selectedApp.id, newFirstDate);

      // Оновлюємо локальний список без повторного запиту
      setApplications((prev) =>
        prev.map((a) =>
          a.id === selectedApp.id ? { ...a, firstDate: newFirstDate } : a
        )
      );

      setSuccessMessage(
        `Дату першої реєстрації для заявки №${selectedApp.number} успішно оновлено!`
      );
      handleCloseModal();
    } catch (err) {
      setModalError('Помилка при оновленні дати. Спробуйте знову.');
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <Card className="shadow-sm m-3">
        <Card.Header as="h5" className="bg-primary text-white">
          Зміна дати первинної реєстрації
        </Card.Header>
        <Card.Body>

          {/* Системні повідомлення */}
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
          {successMessage && (
            <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
              {successMessage}
            </Alert>
          )}

          {/* Форма пошуку */}
          <Form onSubmit={handleSearch} className="mb-4">
            <Row className="align-items-end">
              <Col md={8}>
                <Form.Group controlId="birthCertSearch">
                  <Form.Label>Номер свідоцтва про народження</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Введіть номер свідоцтва (повністю або частково)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mt-2 mt-md-0">
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Пошук...
                    </>
                  ) : (
                    'Знайти заявки'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>

          {/* Таблиця результатів */}
          {applications.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-3">Знайдені заявки ({applications.length}):</h6>
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>№</th>
                      <th>Номер заявки</th>
                      <th>Дата заявки</th>
                      <th>Ім'я дитини</th>
                      <th>Свідоцтво</th>
                      <th>Перша реєстрація</th>
                      <th className="text-center">Дія</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app, i) => (
                      <tr key={app.id || app.number}>
                        <td>{i + 1}</td>
                        <td>{app.number}</td>
                        <td>{formatDate(app.date)}</td>
                        <td>
                          {[app.childLastName, app.childName].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td>{app.birthCertificate || '—'}</td>
                        <td>
                          {app.firstDate ? (
                            <span className="text-success fw-semibold">
                              {formatDate(app.firstDate)}
                            </span>
                          ) : (
                            <span className="text-muted fst-italic">не вказано</span>
                          )}
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-warning"
                            size="sm"
                            title="Змінити дату першої реєстрації"
                            onClick={() => handleOpenModal(app)}
                          >
                            ✏️
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

        </Card.Body>
      </Card>

      {/* ── Модальне вікно редагування ── */}
      <Modal show={!!selectedApp} onHide={handleCloseModal} centered size='xl'>
        <Modal.Header closeButton={!isUpdating}>
          <Modal.Title>Зміна дати першої реєстрації</Modal.Title>
        </Modal.Header>

        {selectedApp && (
          <Modal.Body>
            {modalError && <Alert variant="danger">{modalError}</Alert>}

            <table className="table table-borderless table-sm mb-3">
              <tbody>
                <tr>
                  <th className="text-muted fw-normal" style={{ width: '50%' }}>Номер заявки</th>
                  <td className="fw-semibold">{selectedApp.number || '—'}</td>
                </tr>
                <tr>
                  <th className="text-muted fw-normal">Дата подачі</th>
                  <td>{formatDate(selectedApp.date)}</td>
                </tr>
                <tr>
                  <th className="text-muted fw-normal">Ім'я дитини</th>
                  <td>
                    {[selectedApp.childLastName, selectedApp.childName].filter(Boolean).join(' ') || '—'}
                  </td>
                </tr>
                <tr>
                  <th className="text-muted fw-normal">Дата народження</th>
                  <td>{formatDate(selectedApp.childBirthDate)}</td>
                </tr>
                <tr>
                  <th className="text-muted fw-normal">Свідоцтво</th>
                  <td>{selectedApp.birthCertificate || '—'}</td>
                </tr>
                <tr>
                  <th className="text-muted fw-normal">Поточна дата реєстрації</th>
                  <td>
                    {selectedApp.firstDate ? (
                      <span className="text-success fw-semibold">
                        {formatDate(selectedApp.firstDate)}
                      </span>
                    ) : (
                      <span className="text-muted fst-italic">не вказано</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            <Form.Group controlId="newFirstDate">
              <Form.Label className="fw-bold">
                Нова дата першої реєстрації
              </Form.Label>
              <Form.Control
                type="date"
                value={newFirstDate}
                onChange={(e) => setNewFirstDate(e.target.value)}
                disabled={isUpdating}
              />
            </Form.Group>
          </Modal.Body>
        )}

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={isUpdating}>
            Скасувати
          </Button>
          <Button
            variant="success"
            onClick={handleUpdateFirstDate}
            disabled={isUpdating || !newFirstDate}
          >
            {isUpdating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Оновлюю...
              </>
            ) : (
              'Оновити дату'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FirstDateChangeComponent;